import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import supabase from '../utils/supabaseClient';

const CONTACT_PREF_KEY = 'totheroad_contact_pref';
const CONSENT_KEY = 'totheroad_cookie_consent';

const hasFunctionalConsent = (): boolean => {
  try {
    const consent = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    return consent?.functional === true;
  } catch {
    return false;
  }
};

const Contact: React.FC = () => {
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hasFunctionalConsent()) {
      try {
        const pref = JSON.parse(localStorage.getItem(CONTACT_PREF_KEY) || 'null');
        if (pref) {
          setPrenom(pref.prenom || '');
          setNom(pref.nom || '');
          setEmail(pref.email || '');
        }
      } catch {
        // données corrompues, on ignore
      }
    }
  }, []);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSuccess(null);
    setError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = {
      prenom: String(formData.get('prenom') || '').trim(),
      nom: String(formData.get('nom') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      telephone: String(formData.get('phone') || '').trim() || null,
      sujet: String(formData.get('subject') || '').trim(),
      message: String(formData.get('message') || '').trim(),
    };

    try {
      const { error: insertError } = await supabase
        .from('contact')
        .insert([payload]);

      if (insertError) throw insertError;

      if (hasFunctionalConsent()) {
        localStorage.setItem(
          CONTACT_PREF_KEY,
          JSON.stringify({ prenom: payload.prenom, nom: payload.nom, email: payload.email })
        );
      }

      setSuccess("Votre message a bien été envoyé. Nous reviendrons vers vous rapidement.");
      form.reset();
      setPrenom('');
      setNom('');
      setEmail('');
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de l'envoi. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    /* On ajoute un fond de couleur pale bleuté en légé dégradé */
    <div className="min-h-screen py-10 bg-gradient-to-b from-blue-50 to-blue-200">
      <Helmet>
        <title>Contact — To The Road</title>
        <meta name="description" content="Une question sur la location de van ? Contactez l'équipe To The Road. Départ depuis Versailles, réponse rapide." />
        <link rel="canonical" href="https://totheroad.fr/contact" />
        <meta property="og:title" content="Contact — To The Road" />
        <meta property="og:description" content="Une question sur la location de van ? Contactez l'équipe To The Road. Départ depuis Versailles, réponse rapide." />
        <meta property="og:image" content="https://totheroad.fr/og-image.jpg" />
        <meta property="og:url" content="https://totheroad.fr/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question ? Notre équipe est là pour vous accompagner.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Informations de contact */}
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nos coordonnées</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Téléphone</h3>
                    <p className="text-gray-600">Bientôt disponible</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Email</h3>
                    <a href="mailto:contact@totheroad.fr" className="text-blue-500 hover:text-blue-700 hover:underline transition-colors">contact@totheroad.fr</a>
                  </div>
                </div>
                              <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Lieu de départ</h3>
                    <p className="text-gray-600">Sud ouest Île-de-France, France</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C8 2 5 4 5 8v8l2 2h10l2-2V8c0-4-3-6-7-6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 18v2m8-2v2M8 12h8M8 8h8" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Rejoignez nous</h3>
                    <p className="text-gray-600 mt-1">Accès en transport :</p>
                    <ul className="mt-1 space-y-1">
                      <li>
                        <a
                          href="https://me-deplacer.iledefrance-mobilites.fr/itineraire?arrival=stop_area%253AIDFM%253A63880%257CStopArea%257CVERSAILLES-CHANTIERS%257C%257CVersailles%257C585263.746111457%257C2421891.7001562556%257C%257C%257Cprovider1"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 hover:underline transition-colors"
                        >
                          Gare de Versailles-Chantiers
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://me-deplacer.iledefrance-mobilites.fr/itineraire?arrival=stop_area%253AIDFM%253A73721%257CStopArea%257CVERSAILLES%2520CHATEAU%257C%257CVersailles%257C584750.1339775329%257C2422442.1139497026%257C%257C%257Cprovider1"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 hover:underline transition-colors"
                        >
                          Gare de Versailles-Château-Rive-Gauche
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              {/*Place holder léger gris arrondi pour la cadre google maps */}
              <div className="mt-8 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 shadow-inner p-6">
              <div>{/* Cadre google maps avec pin sur la gare de versaille chantier et le zoom permet de voir l'ile de france*/}
                <iframe
                  title='cadre_map'
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6000!2d2.1289756!3d48.7988118!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e67dc6359a7a01%3A0xa93f45fb6bddca23!2sParc%20de%20stationnement%20-%20Sceaux!5e0!3m2!1sfr!2sfr!4v1765451923196!5m2!1sfr!2sfr"
                  width="100%"
                  height="400"
                  className="rounded-lg"
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade">
                </iframe>
              </div>
              </div>

            </div>
          </div>

                    {/* Formulaire de contact */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
            {success && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
                {success}
              </div>
            )}
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
                {error}
              </div>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom
                </label>
                <input
                  type="text"
                  required
                  id="prenom"
                  name="prenom"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom
                </label>
                <input
                  type="text"
                  required
                  id="nom"
                  name="nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="votre@email.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre numéro de téléphone ( optionnel )"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Sujet
                </label>
                <select
                  required
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="reservation">Réservation</option>
                  <option value="information">Demande d'information</option>
                  <option value="support">Support technique</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  required
                  id="message"
                  name="message"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre message..."
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className={`w-full text-white py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  submitting
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {submitting ? 'Envoi en cours…' : 'Envoyer le message'}
              </button>
            </form>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Contact;